import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Readiness scorecard surface (T-0086 / T-0087 / E08-F04 + E08-F05).
 *
 * The shape mirrors `GetScorecardResult` in
 * `apps/api/src/services/readiness-scorecard.ts`. The radar visualisation
 * (T-0087) consumes the `categories[]` block; the per-criterion table
 * consumes `criteria[]`; the recommendation badge consumes
 * `recommendation` + `total_percent`. See that file for the load-bearing
 * R6 invariant (the LLM never produces these numbers — pure code from
 * weights × scores × thresholds).
 *
 * The Zod schema is intentionally tolerant of `nullable()` for fields
 * that the backend may emit as null until the completeness gate clears
 * (recommendation, aggregate_confidence, total_percent). Per-criterion
 * `score` is a union of number | string | null because the backend
 * carries the raw committed score whose type depends on `score_type`
 * (numeric_0_10 → number, pass_fail / ryg → string).
 */
export const ScoreTypeSchema = z.enum(['numeric_0_10', 'pass_fail', 'ryg']);
export type ScoreType = z.infer<typeof ScoreTypeSchema>;

export const RecommendationSchema = z.enum(['go', 'conditional_go', 'no_go']);
export type Recommendation = z.infer<typeof RecommendationSchema>;

export const ScorecardCategoryAggregateSchema = z.object({
  category: z.string(),
  aggregate_percent: z.number(),
});
export type ScorecardCategoryAggregate = z.infer<typeof ScorecardCategoryAggregateSchema>;

export const ScorecardCriterionResultSchema = z.object({
  criterion_id: z.string(),
  category: z.string(),
  weight: z.number(),
  score_type: ScoreTypeSchema,
  score: z.union([z.number(), z.string(), z.null()]),
  score_normalized: z.number().nullable(),
  weighted_contribution: z.number(),
  excluded_reason: z.enum(['unscored', 'vendor_only_pass']).optional(),
});
export type ScorecardCriterionResult = z.infer<typeof ScorecardCriterionResultSchema>;

export const ReadinessScorecardSchema = z.object({
  total_percent: z.number().nullable(),
  recommendation: RecommendationSchema.nullable(),
  scored_count: z.number(),
  criteria_count: z.number(),
  aggregate_confidence: z.number().nullable(),
  categories: z.array(ScorecardCategoryAggregateSchema),
  criteria: z.array(ScorecardCriterionResultSchema),
  missing_criteria: z.array(z.string()),
  thresholds: z.object({ go: z.number(), conditional: z.number() }),
  computed_at: z.string(),
});
export type ReadinessScorecard = z.infer<typeof ReadinessScorecardSchema>;

export function getReadinessScorecard(engagementId: string): Promise<ReadinessScorecard> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/scorecard`,
    ReadinessScorecardSchema,
  );
}

/**
 * Live scorecard query. `staleTime: 30s` keeps cross-tab navigation
 * snappy (clicking Readiness → Registers → Readiness inside 30s reuses
 * the cached recompute). Score-write mutations elsewhere (Scores tab,
 * once it lands) should `queryClient.invalidateQueries(['readiness',
 * 'scorecard', engagementId])` to force a recompute.
 */
export function useReadinessScorecardQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['readiness', 'scorecard', engagementId] as const,
    queryFn: () => getReadinessScorecard(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}
